import React, { useEffect, useState } from 'react';
import AnimationRevealPage from 'helpers/AnimationRevealPage.js';
import tw from 'twin.macro';
import Header, { LogoLink } from 'components/headers/light.js';
import { SectionHeading } from 'components/misc/Headings';
import { PrimaryButton as PrimaryButtonBase } from 'components/misc/Buttons.js';

const Container = tw.div`relative min-h-screen bg-gray-100`;
const Content = tw.div`max-w-screen-xl mx-auto py-8 lg:py-16 px-4`;
const Card = tw.div`bg-white shadow-lg rounded-lg p-6 mb-6 border border-gray-200`;
const Heading = tw(SectionHeading)`text-center mb-10`;
const SubHeading = tw.h3`text-xl font-bold mb-4 text-gray-800`;
const Label = tw.label`block font-semibold mt-4 mb-2 text-gray-700`;
const Input = tw.input`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500`;
const Select = tw.select`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500`;
const Textarea = tw.textarea`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500`;
const Button = tw(PrimaryButtonBase)`mt-4 w-full`;
const Pre = tw.pre`bg-gray-200 rounded-lg p-4 mt-2 text-xs overflow-x-auto`;
const Row = tw.div`flex flex-wrap -mx-2`;
const Col = tw.div`flex-1 px-2 min-w-0`;

const graphApiVersion = 'v22.0';
const appId = '734185045706086';
const configId = '4022396888034217';
const businessId = '1387130298989894';

export default function WabaAdmin() {
  const [accessToken, setAccessToken] = useState('');
  const [clientBusinessAccountId, setClientBusinessAccountId] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [templateList, setTemplateList] = useState([]);
  const [templateName, setTemplateName] = useState('order_confirmation');
  const [templateLanguage, setTemplateLanguage] = useState('en_US');
  const [templateCategory, setTemplateCategory] = useState('UTILITY');
  const [templateBody, setTemplateBody] = useState(
    `{
      "type": "BODY",
      "text": "Hello {{1}}, your order {{2}} has been confirmed."
    }`
  );
  const [deleteTemplateId, setDeleteTemplateId] = useState('');
  const [loading, setLoading] = useState(false);
  const [fbReady, setFbReady] = useState(false);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [callbackUrl, setCallbackUrl] = useState('');
  const [autoFlowExecuted, setAutoFlowExecuted] = useState(false);
  const [callbackResponse, setCallbackResponse] = useState(null);
  
  // Estados para el flujo automático
  const [flowStatus, setFlowStatus] = useState({
    step1: { status: 'idle', message: '', data: null }, // Listar cuentas cliente
    step2: { status: 'idle', message: '', data: null }, // Listar números
    step3: { status: 'idle', message: '', data: null }, // Recuperar apps suscritas
    step4: { status: 'idle', message: '', data: null }, // Suscribir (si es necesario)
  });

  // Función para limpiar el token
  const cleanToken = (token) => {
    if (!token) return '';
    // Si es un objeto (evento de React), retornar vacío
    if (typeof token === 'object' && token !== null) {
      console.warn('⚠️ cleanToken recibió un objeto, no un string:', token);
      return '';
    }
    // Convertir a string si no lo es
    const tokenStr = typeof token === 'string' ? token : String(token);
    // Remover espacios, saltos de línea, tabs y otros caracteres invisibles
    return tokenStr.trim().replace(/\s+/g, '');
  };

  // Función para enviar datos al padre o hacer GET al callbackUrl
  const sendDataToParent = async (configData) => {
    // Prioridad 1: Hacer GET request a callbackUrl si existe
    if (callbackUrl) {
      console.log('📡 Enviando datos al callbackUrl:', callbackUrl);
      try {
        const url = new URL(callbackUrl);
        url.searchParams.set('accessToken', configData.accessToken);
        url.searchParams.set('clientBusinessAccountId', configData.clientBusinessAccountId);
        url.searchParams.set('phoneNumberId', configData.phoneNumberId);
        url.searchParams.set('phoneNumber', configData.phoneNumber);
        
        console.log('📍 URL final:', url.toString());
        
        const response = await fetch(url.toString(), {
          method: 'GET'
        });
        
        console.log('✅ Respuesta del callback:', response);
        
        // Intentar leer la respuesta
        try {
          const data = await response.json();
          console.log('📝 Datos de respuesta:', data);
          setCallbackResponse({
            status: response.status,
            statusText: response.statusText,
            data: data,
            success: response.ok
          });
        } catch (e) {
          // Si no es JSON, intentar leer como texto
          const text = await response.text();
          setCallbackResponse({
            status: response.status,
            statusText: response.statusText,
            data: text,
            success: response.ok
          });
        }
        
        console.log('✅ Datos enviados al backend exitosamente');
        return;
      } catch (error) {
        console.error('❌ Error al enviar datos al callbackUrl:', error);
      }
    }
    
    // Prioridad 2: PostMessage si estamos en iframe
    if (window.parent && window.parent !== window) {
      console.log('📤 Enviando datos al padre via postMessage:', configData);
      window.parent.postMessage({
        type: 'WABA_CONFIG_COMPLETED',
        data: configData
      }, '*'); // En producción, reemplaza '*' con el origen específico
    } else {
      console.log('ℹ️ No se detectó callbackUrl ni iframe');
    }
  };

  // Flujo automático completo
  const executeAutoFlow = async (tokenToUse = null) => {
    const rawToken = tokenToUse || accessToken;
    const token = cleanToken(rawToken);
    
    if (!token) {
      alert('Por favor ingresa un access token');
      return;
    }

    console.log('🔑 Token limpio a usar:', token);
    console.log('📏 Longitud del token:', token.length);

    setLoading(true);
    
    // Resetear estados
    setFlowStatus({
      step1: { status: 'idle', message: '', data: null },
      step2: { status: 'idle', message: '', data: null },
      step3: { status: 'idle', message: '', data: null },
      step4: { status: 'idle', message: '', data: null },
    });

    try {
      // PASO 1: Listar cuentas del cliente
      setFlowStatus(prev => ({
        ...prev,
        step1: { status: 'loading', message: 'Obteniendo cuentas de cliente...', data: null }
      }));

      const clientAccountsUrl = `https://graph.facebook.com/${graphApiVersion}/${businessId}/client_whatsapp_business_accounts?fields=id,name,currency,owner_business_info&limit=20&access_token=${token}`;
      console.log('🌐 URL del request:', clientAccountsUrl);
      const res1 = await fetch(clientAccountsUrl);
      const data1 = await res1.json();
      
      if (data1.error) {
        setFlowStatus(prev => ({
          ...prev,
          step1: { status: 'error', message: `Error: ${data1.error.message}`, data: null }
        }));
        throw data1.error;
      }

      const clientAccount = data1.data && data1.data.length > 0 ? data1.data[0] : null;
      if (!clientAccount) {
        setFlowStatus(prev => ({
          ...prev,
          step1: { status: 'error', message: 'No se encontraron cuentas de cliente', data: null }
        }));
        throw new Error('No se encontraron cuentas de cliente');
      }

      setClientBusinessAccountId(clientAccount.id);
      setFlowStatus(prev => ({
        ...prev,
        step1: { 
          status: 'success', 
          message: `ID capturado: ${clientAccount.id}`, 
          data: { id: clientAccount.id, name: clientAccount.name, currency: clientAccount.currency }
        }
      }));

      // PASO 2: Listar números de teléfono
      setFlowStatus(prev => ({
        ...prev,
        step2: { status: 'loading', message: 'Obteniendo números de teléfono...', data: null }
      }));

      const phoneNumbersUrl = `https://graph.facebook.com/${graphApiVersion}/${clientAccount.id}/phone_numbers?fields=id,cc,country_dial_code,display_phone_number,verified_name,status,quality_rating,search_visibility,platform_type,code_verification_status&access_token=${token}`;
      const res2 = await fetch(phoneNumbersUrl);
      const data2 = await res2.json();

      if (data2.error) {
        setFlowStatus(prev => ({
          ...prev,
          step2: { status: 'error', message: `Error: ${data2.error.message}`, data: null }
        }));
        throw data2.error;
      }

      const phoneNumberData = data2.data && data2.data.length > 0 ? data2.data[0] : null;
      if (!phoneNumberData) {
        setFlowStatus(prev => ({
          ...prev,
          step2: { status: 'error', message: 'No se encontraron números de teléfono', data: null }
        }));
        throw new Error('No se encontraron números de teléfono');
      }

      setPhoneNumberId(phoneNumberData.id);
      // Guardar el número sin espacios
      const cleanPhoneNumber = phoneNumberData.display_phone_number ? 
        phoneNumberData.display_phone_number.replace(/\s+/g, '') : '';
      setPhoneNumber(cleanPhoneNumber);
      
      setFlowStatus(prev => ({
        ...prev,
        step2: { 
          status: 'success', 
          message: `ID número capturado: ${phoneNumberData.id}`, 
          data: { 
            id: phoneNumberData.id, 
            display_phone_number: cleanPhoneNumber,
            verified_name: phoneNumberData.verified_name,
            status: phoneNumberData.status
          }
        }
      }));

      // PASO 3: Recuperar apps suscritas
      setFlowStatus(prev => ({
        ...prev,
        step3: { status: 'loading', message: 'Verificando apps suscritas...', data: null }
      }));

      const subscribedAppsUrl = `https://graph.facebook.com/${graphApiVersion}/${clientAccount.id}/subscribed_apps?access_token=${token}`;
      const res3 = await fetch(subscribedAppsUrl);
      const data3 = await res3.json();

      if (data3.error) {
        setFlowStatus(prev => ({
          ...prev,
          step3: { status: 'error', message: `Error: ${data3.error.message}`, data: null }
        }));
        throw data3.error;
      }

      const isSubscribed = data3.data && data3.data.length > 0;
      setFlowStatus(prev => ({
        ...prev,
        step3: { 
          status: 'success', 
          message: isSubscribed ? 'App ya está suscrita' : 'App no está suscrita', 
          data: { subscribed: isSubscribed, apps: data3.data || [] }
        }
      }));

      // PASO 4: Suscribir si es necesario
      if (!isSubscribed) {
        setFlowStatus(prev => ({
          ...prev,
          step4: { status: 'loading', message: 'Suscribiendo app...', data: null }
        }));

        const subscribeUrl = `https://graph.facebook.com/${graphApiVersion}/${clientAccount.id}/subscribed_apps`;
        const res4 = await fetch(subscribeUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data4 = await res4.json();

        if (data4.error) {
          setFlowStatus(prev => ({
            ...prev,
            step4: { status: 'error', message: `Error: ${data4.error.message}`, data: null }
          }));
          throw data4.error;
        }

        setFlowStatus(prev => ({
          ...prev,
          step4: { 
            status: 'success', 
            message: 'App suscrita exitosamente', 
            data: data4
          }
        }));
      } else {
        setFlowStatus(prev => ({
          ...prev,
          step4: { 
            status: 'success', 
            message: 'No se requiere suscripción', 
            data: null
          }
        }));
      }

      // ✅ Configuración completada exitosamente - Enviar datos al padre
      const configCompletedData = {
        accessToken: token,
        clientBusinessAccountId: clientAccount.id,
        phoneNumberId: phoneNumberData.id,
        phoneNumber: cleanPhoneNumber
      };
      
      console.log('✅ Configuración completada exitosamente');
      sendDataToParent(configCompletedData);

    } catch (e) {
      console.error('Error en el flujo automático:', e);
      
      let errorMessage = 'Error en el flujo: ';
      if (e.message) {
        errorMessage += e.message;
      } else if (e.error && e.error.message) {
        errorMessage += e.error.message;
      } else {
        errorMessage += JSON.stringify(e);
      }
      
      // Agregar sugerencias según el tipo de error
      if (errorMessage.includes('OAuth') || errorMessage.includes('access token')) {
        errorMessage += '\n\n💡 Sugerencia: Verifica que el token sea válido y no haya expirado. Puedes generar uno nuevo desde el registro de Meta.';
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getTemplates = async () => {
    if (!clientBusinessAccountId) return alert('Primero ejecuta el flujo automático para obtener el ID de cuenta');
    setLoading(true);
    try {
      const token = cleanToken(accessToken);
      const url = `https://graph.facebook.com/${graphApiVersion}/${clientBusinessAccountId}/message_templates?fields=language,name,rejected_reason,status,category,sub_category,last_updated_time,components,quality_score&limit=50&access_token=${token}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.error) throw data.error;
      setTemplateList(data.data || []);
    } catch (e) {
      alert('Error: ' + JSON.stringify(e));
    } finally {
      setLoading(false);
    }
  };

  const createTemplate = async (e) => {
    e.preventDefault();
    if (!clientBusinessAccountId) return alert('Primero ejecuta el flujo automático para obtener el ID de cuenta');
    setLoading(true);
    try {
      const token = cleanToken(accessToken);
      const body = {
        name: templateName,
        language: templateLanguage,
        category: templateCategory,
        components: [JSON.parse(templateBody)],
      };

      const url = `https://graph.facebook.com/${graphApiVersion}/${clientBusinessAccountId}/message_templates?access_token=${token}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.error) throw data.error;
      alert('Plantilla creada correctamente (puede tardar en aparecer activa)');
      getTemplates();
    } catch (e) {
      const obj = JSON.stringify(e);
      const error = obj === '{}' ? e.message : obj;
      alert('Error: ' + error);
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (e) => {
    e.preventDefault();
    if (!deleteTemplateId)
      return alert('Pon el nombre de la plantilla a eliminar');
    if (!clientBusinessAccountId) return alert('Primero ejecuta el flujo automático para obtener el ID de cuenta');
    setLoading(true);
    try {
      const token = cleanToken(accessToken);
      const url = `https://graph.facebook.com/${graphApiVersion}/${clientBusinessAccountId}/message_templates?name=${encodeURIComponent(
        deleteTemplateId
      )}&access_token=${token}`;
      const res = await fetch(url, { method: 'DELETE' });
      const data = await res.json();
      if (data.error) throw data.error;
      alert('Plantilla eliminada');
      getTemplates();
    } catch (e) {
      alert('Error: ' + JSON.stringify(e));
    } finally {
      setLoading(false);
    }
  };

  const fbLoginCallback = async (response) => {
    if (response.authResponse && response.authResponse.code) {
      const code = response.authResponse.code;
      console.log('Código recibido:', code);

      try {
        const url = `https://5558qpocyh.execute-api.us-east-1.amazonaws.com/getAccessTokenMeta`;
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            appId: appId,
            code: code,
          }),
        });

        const data = await res.json();
        if (data.access_token) {
          console.log('Access token:', data.access_token);
          // El flujo automático se ejecutará automáticamente por el useEffect
          setAccessToken(data.access_token);
        } else {
          console.error('Error en el intercambio:', data);
        }
      } catch (err) {
        console.error('Error en fetch:', err);
      }
    } else {
      console.log('Login cancelado o no autorizado');
    }
  };

  const launchWhatsAppSignup = () => {
    if (!fbReady) {
      alert('El SDK de Facebook aún no está listo.');
      return;
    }

    window.FB.login(fbLoginCallback, {
      config_id: configId,
      response_type: 'code',
      override_default_response_type: true,
      redirect_uri: 'https://madritech.co/waba-admin',
      extras: {
        setup: {},
        featureType: 'whatsapp_business_app_onboarding',
        sessionInfoVersion: '3',
      },
    });
  };

  useEffect(() => {
    if (document.getElementById('facebook-jssdk')) return;

    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => setSdkLoaded(true);
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (sdkLoaded) {
      window.FB.init({
        appId,
        autoLogAppEvents: true,
        xfbml: false,
        version: 'v23.0',
      });
      console.log('window.FB.init');
      setFbReady(true);
    }
  }, [sdkLoaded]);

  // Leer callbackUrl de los parámetros de la URL al montar el componente
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const callback = urlParams.get('callbackUrl');
    if (callback) {
      setCallbackUrl(callback);
      console.log('🔗 CallbackUrl detectada:', callback);
    }
  }, []);

  // Ejecutar flujo automático cuando se capture el accessToken (solo una vez)
  useEffect(() => {
    if (accessToken && accessToken.length > 0 && !autoFlowExecuted) {
      console.log('🔑 Token detectado, ejecutando flujo automático...');
      setAutoFlowExecuted(true);
      executeAutoFlow(accessToken);
    }
  }, [accessToken, autoFlowExecuted]);

  // Componente para mostrar el estado de cada paso
  const StepCard = ({ stepNumber, title, status, message, data }) => {
    const getStatusColor = () => {
      switch (status) {
        case 'loading': return '#3b82f6';
        case 'success': return '#10b981';
        case 'error': return '#ef4444';
        default: return '#9ca3af';
      }
    };

    const getStatusIcon = () => {
      switch (status) {
        case 'loading': return '⏳';
        case 'success': return '✓';
        case 'error': return '✗';
        default: return '○';
      }
    };

    return (
      <div style={{
        background: '#fff',
        border: `2px solid ${getStatusColor()}`,
        borderRadius: 12,
        padding: 16,
        marginBottom: 12,
        transition: 'all 0.3s ease'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ 
            fontSize: 24, 
            marginRight: 12,
            color: getStatusColor()
          }}>
            {getStatusIcon()}
          </span>
          <div>
            <div style={{ fontWeight: 'bold', fontSize: 16, color: '#1f2937' }}>
              Paso {stepNumber}: {title}
            </div>
            <div style={{ fontSize: 14, color: '#6b7280', marginTop: 4 }}>
              {message || 'Esperando...'}
            </div>
          </div>
        </div>
        {data && (
          <div style={{
            background: '#f9fafb',
            borderRadius: 8,
            padding: 12,
            marginTop: 8,
            fontSize: 13,
            fontFamily: 'monospace'
          }}>
            {Object.entries(data).map(([key, value]) => (
              <div key={key} style={{ marginBottom: 4 }}>
                <strong>{key}:</strong> {typeof value === 'object' ? JSON.stringify(value) : value}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <AnimationRevealPage>
      <Header
        logoLink={
          <LogoLink href='/'>
            <img
              src='/logo192.png'
              alt='Logo Madritech'
              style={{ height: 40 }}
            />
            Madritech
          </LogoLink>
        }
        links={[]}
        collapseBreakpointClass='xl'
      />
      <Container>
        <Content>
          <div style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 16,
            padding: '40px 20px',
            marginBottom: 32,
            textAlign: 'center',
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
          }}>
            <h1 style={{ 
              color: 'white', 
              fontSize: 36, 
              fontWeight: 'bold',
              marginBottom: 12,
              textShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              WhatsApp Business API Admin
            </h1>
            <p style={{ 
              color: 'rgba(255,255,255,0.9)', 
              fontSize: 18,
              maxWidth: 600,
              margin: '0 auto'
            }}>
              Gestiona tu integración con WhatsApp Business de forma fácil y rápida
            </p>
          </div>
          
          {/* Sección de Registro */}
          <Card>
            <SubHeading>🚀 Paso 1: Iniciar Registro</SubHeading>
            <p style={{ marginBottom: 16, color: '#6b7280', lineHeight: 1.6 }}>
              Haz clic en el botón para comenzar el proceso de vinculación con WhatsApp Cloud. 
              Esto te redirigirá a Facebook para autorizar la aplicación.
            </p>
            <Button
              onClick={launchWhatsAppSignup}
              disabled={!appId || !configId}
            >
              Iniciar registro con Meta
            </Button>
          </Card>

          {/* Sección de Access Token y Flujo Automático */}
          <Card>
            <SubHeading>🔑 Paso 2: Configuración y Flujo Automático</SubHeading>
            <Label>Access Token</Label>
            <Input
              type='text'
              value={accessToken}
              onChange={(e) => {
                const cleaned = cleanToken(e.target.value);
                console.log('📝 Token ingresado (limpio):', cleaned);
                console.log('📏 Longitud:', cleaned.length);
                setAccessToken(cleaned);
              }}
              onPaste={(e) => {
                const pastedText = e.clipboardData.getData('text');
                console.log('📋 Token pegado (original):', pastedText);
                const cleaned = cleanToken(pastedText);
                console.log('📋 Token pegado (limpio):', cleaned);
                console.log('📏 Longitud:', cleaned.length);
              }}
              placeholder='Tu access token (se captura automáticamente o pégalo aquí)'
              autoComplete='off'
            />
            {accessToken && (
              <div style={{ fontSize: 12, color: '#10b981', marginTop: 8 }}>
                ✓ Token válido ({accessToken.length} caracteres)
              </div>
            )}

            <Button onClick={() => executeAutoFlow()} disabled={loading || !accessToken}>
              {loading ? '⏳ Ejecutando flujo...' : '▶ Ejecutar Flujo Automático'}
            </Button>

            {/* Mostrar progreso del flujo */}
            {(flowStatus.step1.status !== 'idle' || 
              flowStatus.step2.status !== 'idle' || 
              flowStatus.step3.status !== 'idle' || 
              flowStatus.step4.status !== 'idle') && (
              <div style={{ marginTop: 24 }}>
                <h4 style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 16, color: '#1f2937' }}>
                  📊 Progreso del Flujo
                </h4>
                <StepCard
                  stepNumber={1}
                  title="Listar Cuentas Cliente"
                  status={flowStatus.step1.status}
                  message={flowStatus.step1.message}
                  data={flowStatus.step1.data}
                />
                <StepCard
                  stepNumber={2}
                  title="Listar Números de Teléfono"
                  status={flowStatus.step2.status}
                  message={flowStatus.step2.message}
                  data={flowStatus.step2.data}
                />
                <StepCard
                  stepNumber={3}
                  title="Verificar Apps Suscritas"
                  status={flowStatus.step3.status}
                  message={flowStatus.step3.message}
                  data={flowStatus.step3.data}
                />
                <StepCard
                  stepNumber={4}
                  title="Suscribir App (si es necesario)"
                  status={flowStatus.step4.status}
                  message={flowStatus.step4.message}
                  data={flowStatus.step4.data}
                />
              </div>
            )}

            {/* Mostrar IDs capturados */}
            {clientBusinessAccountId && (
              <div style={{ 
                marginTop: 20, 
                padding: 16, 
                background: '#ecfdf5', 
                borderRadius: 8,
                border: '1px solid #10b981'
              }}>
                <div style={{ fontWeight: 'bold', color: '#065f46', marginBottom: 8 }}>
                  ✓ Configuración Completada
                </div>
                <div style={{ fontSize: 14, color: '#047857', marginBottom: 4 }}>
                  <strong>Access Token:</strong> {accessToken.substring(0, 30)}...{accessToken.substring(accessToken.length - 10)}
                </div>
                <div style={{ fontSize: 14, color: '#047857', marginBottom: 4 }}>
                  <strong>ID Cuenta Cliente:</strong> {clientBusinessAccountId}
                </div>
                {phoneNumberId && (
                  <div style={{ fontSize: 14, color: '#047857', marginBottom: 4 }}>
                    <strong>ID Número:</strong> {phoneNumberId}
                  </div>
                )}
                {phoneNumber && (
                  <div style={{ fontSize: 14, color: '#047857', marginTop: 4 }}>
                    <strong>Número:</strong> {phoneNumber}
                  </div>
                )}
              </div>
            )}

            {/* Mostrar respuesta del callback */}
            {callbackResponse && (
              <div style={{ 
                marginTop: 20, 
                padding: 16, 
                background: callbackResponse.success ? '#ecfdf5' : '#fef2f2', 
                borderRadius: 8,
                border: `1px solid ${callbackResponse.success ? '#10b981' : '#ef4444'}`
              }}>
                <div style={{ fontWeight: 'bold', color: callbackResponse.success ? '#065f46' : '#991b1b', marginBottom: 8 }}>
                  {callbackResponse.success ? '✅ Respuesta del Callback' : '❌ Error en Callback'}
                </div>
                <div style={{ fontSize: 14, color: callbackResponse.success ? '#047857' : '#991b1b', marginBottom: 4 }}>
                  <strong>Status:</strong> {callbackResponse.status} {callbackResponse.statusText}
                </div>
                {callbackResponse.data && (
                  <div style={{ 
                    marginTop: 12,
                    background: callbackResponse.success ? '#f0fdf4' : '#fef2f2',
                    padding: 12,
                    borderRadius: 6,
                    fontSize: 13,
                    fontFamily: 'monospace',
                    maxHeight: 300,
                    overflow: 'auto'
                  }}>
                    <strong>Respuesta:</strong>
                    <pre style={{ margin: '8px 0 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {typeof callbackResponse.data === 'string' 
                        ? callbackResponse.data 
                        : JSON.stringify(callbackResponse.data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Sección de Plantillas */}
          <Card>
            <SubHeading>📋 Gestión de Plantillas</SubHeading>
            <p style={{ marginBottom: 16, color: '#6b7280', lineHeight: 1.6 }}>
              Administra las plantillas de mensajes de WhatsApp. Estas operaciones son manuales.
            </p>
            <Button onClick={getTemplates} disabled={loading || !clientBusinessAccountId}>
              {loading ? '⏳ Cargando...' : '📄 Listar Plantillas'}
            </Button>
            
            {templateList.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <h4 style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 16, color: '#1f2937' }}>
                  Plantillas Disponibles ({templateList.length})
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                  {templateList.map((tpl, idx) => (
                    <div
                      key={tpl.id || tpl.name || idx}
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        borderRadius: 12,
                        padding: 20,
                        minWidth: 280,
                        maxWidth: 360,
                        marginBottom: 8,
                        flex: '1 1 280px',
                        transition: 'transform 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                      <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>
                        {tpl.name}
                      </div>
                      <div style={{ 
                        fontSize: 13, 
                        opacity: 0.9, 
                        marginBottom: 8,
                        display: 'flex',
                        gap: 8,
                        flexWrap: 'wrap'
                      }}>
                        <span style={{ 
                          background: 'rgba(255,255,255,0.2)', 
                          padding: '4px 8px', 
                          borderRadius: 4 
                        }}>
                          {tpl.language}
                        </span>
                        <span style={{ 
                          background: 'rgba(255,255,255,0.2)', 
                          padding: '4px 8px', 
                          borderRadius: 4 
                        }}>
                          {tpl.category}
                        </span>
                        <span style={{ 
                          background: tpl.status === 'APPROVED' ? '#10b981' : '#f59e0b', 
                          padding: '4px 8px', 
                          borderRadius: 4,
                          fontSize: 12,
                          fontWeight: 600
                        }}>
                          {tpl.status}
                        </span>
                      </div>
                      {tpl.quality_score && (
                        <div style={{ fontSize: 12, opacity: 0.9, marginBottom: 8 }}>
                          <strong>Calidad:</strong> {JSON.stringify(tpl.quality_score)}
                        </div>
                      )}
                      <div style={{ fontSize: 13, marginTop: 12, marginBottom: 8 }}>
                        {tpl.components && tpl.components.map((c, i) => (
                          c.type === 'BODY' && (
                            <div key={i} style={{ 
                              background: 'rgba(255,255,255,0.15)', 
                              padding: 12, 
                              borderRadius: 8,
                              fontSize: 12,
                              lineHeight: 1.5
                            }}>
                              {c.text}
                            </div>
                          )
                        ))}
                      </div>
                      <details style={{ fontSize: 11, opacity: 0.8, marginTop: 12 }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Ver detalles completos</summary>
                        <Pre style={{ 
                          background: 'rgba(0,0,0,0.3)', 
                          color: 'white',
                          marginTop: 8,
                          maxHeight: 200,
                          overflow: 'auto'
                        }}>
                          {JSON.stringify(tpl, null, 2)}
                        </Pre>
                      </details>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Crear Plantilla */}
          <Card>
            <SubHeading>➕ Crear Nueva Plantilla</SubHeading>
            <form onSubmit={createTemplate}>
              <Row>
                <Col>
                  <Label>Nombre de la Plantilla</Label>
                  <Input
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    required
                    placeholder='ej: order_confirmation'
                  />
                </Col>
                <Col>
                  <Label>Idioma</Label>
                  <Input
                    value={templateLanguage}
                    onChange={(e) => setTemplateLanguage(e.target.value)}
                    required
                    placeholder='ej: es_ES, en_US'
                  />
                </Col>
              </Row>
              
              <Label>Categoría</Label>
              <Select
                value={templateCategory}
                onChange={(e) => setTemplateCategory(e.target.value)}
                required
              >
                <option value='UTILITY'>UTILITY - Notificaciones transaccionales</option>
                <option value='MARKETING'>MARKETING - Promociones y ofertas</option>
                <option value='AUTHENTICATION'>AUTHENTICATION - Códigos de verificación</option>
              </Select>
              
              <Label>Componente del Cuerpo (JSON)</Label>
              <Textarea
                rows={6}
                value={templateBody}
                onChange={(e) => setTemplateBody(e.target.value)}
                required
                placeholder='{"type": "BODY", "text": "Hola {{1}}, tu pedido {{2}} ha sido confirmado."}'
                style={{ fontFamily: 'monospace', fontSize: 13 }}
              />
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 8 }}>
                💡 Usa {`{{1}}, {{2}}`}, etc. para variables dinámicas
              </div>
              
              <Button type='submit' disabled={loading || !clientBusinessAccountId}>
                {loading ? '⏳ Creando...' : '✓ Crear Plantilla'}
              </Button>
            </form>
          </Card>

          {/* Eliminar Plantilla */}
          <Card>
            <SubHeading>🗑️ Eliminar Plantilla</SubHeading>
            <form onSubmit={deleteTemplate}>
              <Label>Nombre de la Plantilla a Eliminar</Label>
              <Input
                value={deleteTemplateId}
                onChange={(e) => setDeleteTemplateId(e.target.value)}
                required
                placeholder='ej: order_confirmation'
              />
              <div style={{ fontSize: 13, color: '#ef4444', marginTop: 8, marginBottom: 8 }}>
                ⚠️ Esta acción no se puede deshacer
              </div>
              <Button 
                type='submit' 
                disabled={loading || !clientBusinessAccountId}
                style={{ background: '#ef4444' }}
              >
                {loading ? '⏳ Eliminando...' : '🗑️ Eliminar Plantilla'}
              </Button>
            </form>
          </Card>
        </Content>
      </Container>
    </AnimationRevealPage>
  );
}
